import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs';
import { SharedModule } from '../../../../shared/shared.module';
import {
  NavigationItem,
  navigationItemsInvoices,
} from '../../../../models/navigation-item-model';
import { LabelService } from '../../../../services/label.service';
import { classes, pages } from '../../../../shared/labels';
import { StsCreditNotePayload } from '../sts-models';
import { StsCreditNoteService } from '../sts-credit-note.service';
import { StsInvoiceCalculationsService } from '../sts-invoice-calculations.service';
import { InvoicesService } from '../../../../services/invoices.service';
import { ErrorPanelService } from '../../../../services/error-panel.service';

@Component({
  selector: 'app-issue-sts-credit-note',
  standalone: true,
  imports: [
    SharedModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
  ],
  templateUrl: './issue-sts-credit-note.component.html',
  styleUrl: './issue-sts-credit-note.component.scss',
})
export class IssueStsCreditNoteComponent implements OnInit {
  navigationItems: NavigationItem[];
  creditNoteForm!: FormGroup;
  isLoading = true;
  isSubmitting = false;

  constructor(
    public labelService: LabelService,
    private router: Router,
    private fb: FormBuilder,
    private creditNoteService: StsCreditNoteService,
    private stsCalculationsService: StsInvoiceCalculationsService,
    private invoicesService: InvoicesService,
    private errorPanelService: ErrorPanelService
  ) {
    this.navigationItems = navigationItemsInvoices.map((item) => {
      if (item.label === pages.invoices_list) {
        return { ...item, class: classes.hnb };
      }
      return { ...item };
    });
  }

  ngOnInit(): void {
    if (!this.creditNoteService.hasSource()) {
      this.router.navigate(['/fatture']);
      return;
    }

    this.createForm();
    this.loadNextNumber();
  }

  private createForm(): void {
    this.creditNoteForm = this.fb.group({
      tipo: [{ value: 'Nota di Credito STS' as const, disabled: true }],
      numero_fattura_originale: [
        {
          value: this.creditNoteService.numero_fattura_originale,
          disabled: true,
        },
        Validators.required,
      ],
      progressivo_numero: [
        null,
        [Validators.required, Validators.pattern(/^\d{3}$/)],
      ],
      importo: [null, [Validators.required, Validators.min(0.01)]],
    });
  }

  private loadNextNumber(): void {
    this.isLoading = true;

    this.invoicesService.getSTSNextNumber().subscribe({
      next: (nextNumber) => {
        this.creditNoteForm
          .get('progressivo_numero')
          ?.setValue(nextNumber.progressivo_numero);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading STS next number for credit note:', error);
        this.errorPanelService.showErrorSnackBar(
          'Errore durante il caricamento del numero documento'
        );
        this.isLoading = false;
        this.onCancel();
      },
    });
  }

  private getApiErrorMessage(err: unknown, fallback: string): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (typeof body === 'string' && body.trim()) {
        return body;
      }
      if (body && typeof body === 'object') {
        const message =
          (body as { message?: string }).message ??
          (body as { detail?: string }).detail ??
          (body as { error?: string }).error;
        if (message && String(message).trim()) {
          return String(message);
        }
      }
    }
    return fallback;
  }

  onCancel(): void {
    this.creditNoteService.clear();
    this.router.navigate(['/fatture']);
  }

  onSubmit(): void {
    if (this.isSubmitting) {
      return;
    }

    this.stsCalculationsService.markFormGroupTouched(this.creditNoteForm);

    if (!this.creditNoteForm.valid) {
      this.errorPanelService.showErrorSnackBar(
        'Compila tutti i campi obbligatori prima di inviare'
      );
      return;
    }

    const sourceInvoiceId = this.creditNoteService.sourceInvoiceId;
    if (!sourceInvoiceId) {
      this.router.navigate(['/fatture']);
      return;
    }

    const formValue = this.creditNoteForm.getRawValue();
    const payload: StsCreditNotePayload = {
      tipo: 'Nota di Credito STS',
      numero_fattura_originale: String(formValue.numero_fattura_originale),
      progressivo_numero: `${formValue.progressivo_numero}`,
      importo: Number(formValue.importo),
    };

    this.isSubmitting = true;

    this.invoicesService
      .sendSTSCreditNote(payload, sourceInvoiceId)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: () => {
          this.errorPanelService.showInfoSnackBar(
            'La nota di credito è stata inviata con successo.'
          );
          this.creditNoteService.clear();
          this.router.navigate(['/fatture']);
        },
        error: (err) => {
          console.error(err);
          this.errorPanelService.showErrorSnackBar(
            this.getApiErrorMessage(
              err,
              "Errore nell'invio della nota di credito."
            )
          );
        },
      });
  }
}
